import React, { useState, useEffect } from 'react'

import {
    StyleSheet,
    View,
    Animated,
    RefreshControl,
    ScrollView,
    Platform,
    Text,
    Dimensions,
    Image
} from 'react-native'

import { useSafeAreaInsets } from 'react-native-safe-area-context'

import cloneDeep from 'lodash/cloneDeep'

import { useContent } from '@sart/content'

import client, { useClient } from '@sart/client'
import {
    adaptive,
    colors,
    backgroundColors,
    typography,
    radius,
    shadows
} from '@sart/styles'
import {
    Description,
    Space,
    MoonDial,
    ProductRecurring,
    ProductAddon,
    ProductNoPlan,
    BasePlan,
    PendingCashPayment,
    Price,
    ActivationPrompt,
} from '@sart/components'
import {
    Logo, CompanyIcon, Mobile
} from '@sart/icons'
import {
    getCoreBalance,
    postpaidBalanceContainer,
    setupBalances,
    getDataBalanceQuantity,
    getDataBalanceStartingQuantity,
    getMinutesBalanceQuantity,
    getMinutesBalanceStartingQuantity,
    getTextBalanceQuantity,
    getTextBalanceStartingQuantity,
    formatBytes
} from '@sart/utilities'
import { post } from 'superagent'

const BRANDING_HEIGHT = 24
const MAX_DIALS_HEIGHT = 120
const MIN_DIALS_HEIGHT = 48
const EXPIRATION_HEIGHT = 24
const HALF_PADDING = 12

export default function PostpaidDashboard(props) {
    const { account, service, navigation, reloadData } = props
    const content = useContent()

    const [data, setData] = useState({})

    const balanceContainer = cloneDeep(postpaidBalanceContainer)

    const {
        cache: {
            loading,
            customer,
            postpaidLatestInvoice,
            postpaidBalances,
            postpaidProvisionedOneOffAddOns,
            postpaidProvisionedRecurringAddOns,
            postpaidOpenCashPayments,
            accounts,
						language
        } = {}
    } = useClient()
    const insets = useSafeAreaInsets()

    useEffect(() => {
        loadBalancesAndInvoices()
        loadCustomerData()
    }, [])

    async function loadBalancesAndInvoices() {
        try {
						client.loadBalancesAndInvoicesPostpaid(account)
						const planProducts = await client.callApi('service', 'getServicePlanProductInstances',[account.accountId, service != undefined ? service.serviceId : account.services[0].serviceId])
						setData({
							planProduct: planProducts[0]
						})
						if(client.cache.serviceId === undefined) {
							client.setCache({
								serviceId:service.serviceId
							})
						}
        } catch (error) {
            // console.log('error is: ' + JSON.stringify(error, null, 2))
        }
    }
		
    function loadCustomerData() {
        //If the customer has not been loaded yet, load it
        //Mainly here for web app refresh issue SRPRODDEV-386
        if (customer === undefined) {
            client.loadPersonalDetails()
        }
    }

    function cancelRecurringAddOn(addOn) {
        //set up the basket with the del item
        // const order = client.transactionServiceDelItem(addOn)
        // if (order.status === 'successful) {
        // console.log('first addOn is: ');
        // console.log(addOn);
        // const confirmation = {
        //   title: `${addOn.product.productName}`,
        //   description: `${content.dashboard.cancelDescription}`
        // }
        const { instanceLegacyId: instanceId } = addOn
        navigation.navigate('ConfirmCancel', { addOn, instanceId })
        //}
    }

    const MAX_HEADER_HEIGHT = BRANDING_HEIGHT + MAX_DIALS_HEIGHT + EXPIRATION_HEIGHT
    const MIN_HEADER_HEIGHT = MIN_DIALS_HEIGHT
    const HEADER_HEIGHT_DELTA = MAX_HEADER_HEIGHT - MIN_HEADER_HEIGHT

    const scrollY = new Animated.Value(0)

    const headerY = scrollY.interpolate({
        inputRange: [0, HEADER_HEIGHT_DELTA],
        outputRange: [0, 1],
        extrapolateRight: 'clamp' // Animated.Extrapolate.CLAMP
    })

    const translateY = headerY.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 36],
        extrapolateLeft: 'clamp' // Animated.Extrapolate.CLAMP
    })

    const headerHeight = headerY.interpolate({
        inputRange: [0, 1],
        outputRange: [MAX_HEADER_HEIGHT, MIN_HEADER_HEIGHT],
        extrapolateLeft: 'clamp' // Animated.Extrapolate.CLAMP
    })

    const spacerHeight = headerY.interpolate({
        inputRange: [0, 1],
        outputRange: [0, HEADER_HEIGHT_DELTA],
    })

    const hide = headerY.interpolate({
        inputRange: [0, 0.7],
        outputRange: [1, 0],
    })

    const show = headerY.interpolate({
        inputRange: [0.7, 1],
        outputRange: [0, 1],
    })

    const smallDialSize = headerY.interpolate({
        inputRange: [0, 1],
        outputRange: [106, 48],
        extrapolate: 'clamp'
    })

    const bigDialSize = headerY.interpolate({
        inputRange: [0, 1],
        outputRange: [125, 48],
        extrapolate: 'clamp'
    })
		const UNITS = [
			'B',
			'KB',
			'MB',
			'GB',
			'TB',
			'PB',
			'EB',
			'ZB',
			'YB'
		];

		function getBalanceQuantity(balance) {
			if(balance !== undefined) {
				return balance.quantity !== undefined ? balance.quantity : 0
			}
			return 0
		}

		function getBalanceStartingQuantity(balance) {
			if(balance !== undefined) {
				return balance.startingQuantity !== undefined ? balance.startingQuantity : 0
			}
			if(balance < 0) {
				return balance.startingQuantity < 0 ? balance.startingQuantity : 0
			}
			return 0
		}

		function formatBytes(value, balances, options) {
			if (!Number.isFinite(value)) {
				throw new TypeError(`Expected a finite number, got ${typeof value}: ${value}`);
			}
			options = Object.assign({}, options);
		
			if(value === 0) {
				return ' 0B';
			}
		
			const isNegative = value < 0;
			const prefix = isNegative ? '-' : '';
		
			if (isNegative) {
				return value;
			}
		
			if (value < 1) {
				const numberString = toLocaleString(value, options.locale);
				return prefix + numberString + 'B';
			}
			// const exponent = Math.min((Math.floor(Math.log10(value) / 3) + 1), UNITS.length - 1);
			// Setting the exponent to 3 keeps the units to GB, the line above displays GB if above 1 GB and if belowm shows MB unit.
			const exponent = 3;
			const unit = UNITS[exponent];
			value = Number((value / getConversionRateData(balances, unit)).toPrecision(2));
			const numberString = toLocaleString(value, options.locale);
			
			return prefix + numberString + '' + unit;
		
		}
		/*
		Formats the given number using `Number#toLocaleString`.
		- If locale is a string, the value is expected to be a locale-key (for example: `de`).
		- If locale is true, the system default locale is used for translation.
		- If no value for locale is specified, the number is returned unmodified.
		*/
		const toLocaleString = (number, locale) => {
			let result = number;
			if (typeof locale === 'string') {
				result = number.toLocaleString(locale);
			} else if (locale === true) {
				result = number.toLocaleString();
			}
		
			return result;
		};

		function getConversionRateData(balances, unit) {
			if(balances?.data?.balanceDefinition?.conversionRates) {
				const conversionRates = balances.data.balanceDefinition.conversionRates
				const rate = conversionRates.filter(function (rate) {
					if(unit === 'GB') {
					return rate.unit === 'GB'
					}
					if(unit === 'MB') {
						return rate.unit === 'MB'
					}
				})
				return rate[0].rate
			} else {
				return 1024
			}
		}



    return (
        <View>
            <Animated.ScrollView
                scrollEventThrottle={1}
                // stickyHeaderIndices={[0,]}
                showsVerticalScrollIndicator={true}
                style={styles.container}
                onScroll={
                    Animated.event([
                        { nativeEvent: { contentOffset: { y: scrollY } } }
                    ],{})
                }
            >
                <Animated.View
                    style={styles.headerContainer}
                >
                    <Animated.View
                        style={[styles.headerContent, { height: headerHeight }]}
                    >
                        <Animated.View
                            style={[styles.branding, { transform: [{ translateY: translateY }] }]}
                        >
                        </Animated.View>
                        {Platform.OS === 'ios' &&
                            <Space height={24} />
                        }
                        <View style={styles.rowContainer}>
                            <View style={{ justifyContent: 'flex-end' }}>
                                <Image
                                    source={{ uri: 'https://shop.bikermobile.com/assets/biker_mobile_logo_final_nobg.png' }}
                                    height={45} width={45}
                                />
                                {/* <CompanyIcon color={colors.srGreen} height={60} width={60} /> */}
                            </View>
                            <View style={styles.amountDue}>
                                <Space height={48} />
                                <Text style={styles.amountDueTitle}>{content.dashboard.amountDue}</Text>
                                {postpaidLatestInvoice &&
                                    <Price style={styles.amountDueText} value={postpaidLatestInvoice.outstandingAmountDue} code={postpaidLatestInvoice.currency} />
                                }
                            </View>
                        </View>
                        <View style={styles.headerMain}>
                            <View>
                                {accounts && accounts[0] && accounts[0].services[0] &&
                                    <View>
                                        <Space height={12} />
                                        <View style={styles.numberContainer}>
                                            <Mobile color={colors.srTextInput} height={26} width={11}/>
                                            <Space width={10} />
                                            <Text style={styles.numberText}>{service.lineIdentifier}</Text>
                                        </View>
                                        <Space height={12} />
                                    </View>
                                }
                                <Animated.View style={[styles.dials, { height: bigDialSize }]}>
                                    <MoonDial
                                        name={content.navigation.texts}
                                        size={smallDialSize}
                                        value={getBalanceQuantity(postpaidBalances?.text)}
                                        range={getBalanceStartingQuantity(postpaidBalances?.text)}
                                        loading={loading}
                                        onPress={() => void navigation.navigate('PostpaidTexts')}
                                        style={styles.moondialStyle}
                                        strokeColour={colors.brightOrange + 20}
                                        mostlyFullColour={colors.brightOrange}
                                        almostEmptyColour={colors.brightOrange}
                                        mostlyFullTextColour={colors.white}
                                        almostEmptyTextColour={colors.white}
                                        textColour={colors.white}
																				injectedContent={content}
                                    />

                                    <Space width={12} />

                                    <MoonDial
                                        name={content.navigation.data}
                                        formatValue={val => formatBytes(val, postpaidBalances)}
                                        formatRange={val => formatBytes(val, postpaidBalances)}
                                        size={bigDialSize}
                                        value={getBalanceQuantity(postpaidBalances?.data)}
                                        range={getBalanceStartingQuantity(postpaidBalances?.data)}
                                        loading={loading}
                                        onPress={() => void navigation.navigate('PostpaidData')}
                                        style={styles.moondialStyle}
                                        strokeColour={colors.brightOrange + 20}
                                        mostlyFullColour={colors.brightOrange}
                                        almostEmptyColour={colors.brightOrange}
                                        mostlyFullTextColour={colors.white}
                                        almostEmptyTextColour={colors.white}
                                        textColour={colors.white}
																				injectedContent={content}
                                    />

                                    <Space width={12} />

                                    <MoonDial
                                        name={content.navigation.minutes}
                                        size={smallDialSize}
                                        value={getBalanceQuantity(postpaidBalances?.voice)}
                                        range={getBalanceStartingQuantity(postpaidBalances?.voice)}
                                        loading={loading}
                                        onPress={() => void navigation.navigate('PostpaidMinutes')}
                                        style={styles.moondialStyle}
                                        strokeColour={colors.brightOrange + 20}
                                        mostlyFullColour={colors.brightOrange}
                                        almostEmptyColour={colors.brightOrange}
                                        mostlyFullTextColour={colors.white}
                                        almostEmptyTextColour={colors.white}
                                        textColour={colors.white}
																				injectedContent={content}
                                    />
                                </Animated.View>
                            </View>
                        </View>

                    </Animated.View>

                    <Animated.View style={[styles.line, { opacity: show }]} />
                </Animated.View>

                {/* <Animated.View style={{ height: spacerHeight }} /> */}
                <Space height={24} />
                {!loading &&
                    <ActivationPrompt
                                titleStyle={styles.titleStyle}
                                cashContainer={styles.cashContainer}
                                priceStyle={styles.priceStyle}
                                descriptionStyle={styles.descriptionStyle}
                                descriptionContentStyle={styles.descriptionContentStyle}
                                dateStyle={styles.dateStyle}
                                buttonTitleStyle={styles.buttonTitleStyle}
                                buttonStyle={styles.buttonStyle}
                                price={"£" + 0}
                                creationDate={new Date()}
                                daysToExpire={3}
                                onPress={() => void navigation.navigate('ViewCashBarcode', { })}
																language={language}
																injectedContent={content}
                            />
                
                }

                <View style={styles.content}>
                    {postpaidOpenCashPayments && postpaidOpenCashPayments.map((confirmedBasket, i) =>
                        <React.Fragment key={i}>
                            <PendingCashPayment
                                titleStyle={styles.titleStyle}
                                cashContainer={styles.cashContainer}
                                priceStyle={styles.priceStyle}
                                descriptionStyle={styles.descriptionStyle}
                                descriptionContentStyle={styles.descriptionContentStyle}
                                dateStyle={styles.dateStyle}
                                buttonTitleStyle={styles.buttonTitleStyle}
                                buttonStyle={styles.buttonStyle}
                                price={"£" + (confirmedBasket.total / 100).toFixed(2)}
                                creationDate={new Date(confirmedBasket.creationDate)}
                                daysToExpire={3}
                                onPress={() => void navigation.navigate('ViewCashBarcode', { confirmedBasket })}
																language={language}
																injectedContent={content}
                            />
                            <Space height={24} />
                        </React.Fragment>

                    )}

                    {data.planProduct &&
                        <View>
                            <BasePlan
                                product={data.planProduct}
                                productContainer={styles.planContainer}
                                labelStyle={styles.descriptionStyle}
                                nameStyle={styles.descriptionContentStyle}
                                textStyle={styles.dateStyle}
                                buttonStyle={styles.buttonStyle}
                                buttonTitleStyle={styles.buttonTitleStyle}
                                productName={data.planProduct.product.productName}
                                price={data.planProduct.product.recurringCost}
                                code={data.planProduct.product.currency}
                                showChange={true}
																language={language}
																injectedContent={content}
                            />
                        </View>
                    }

                    <Space height={24} />

                    {postpaidProvisionedRecurringAddOns && postpaidProvisionedRecurringAddOns.map((addOn, i) =>
                        <React.Fragment key={i}>
                            <ProductRecurring
                                product={{
                                    type: 'plan',
                                    name: addOn.product.productName,
                                    price: addOn.product.recurringCost,
                                    expiration: addOn.expiryDateTime,
                                    startDate: addOn.startDateTime,
                                    category: 'recurring',
                                    code: addOn.product.currency,
                                    frequency: addOn.product.recurringFrequency,
                                    frequencyUnit: addOn.product.recurringFrequencyUnit,
                                }}
                                onEditProduct={() => void navigation.navigate('Plans')}
                                onCancelProduct={() => { cancelRecurringAddOn(addOn) }}
                                productContainer={styles.cashContainer}
                                labelStyle={styles.descriptionStyle}
                                nameStyle={styles.descriptionContentStyle}
                                textStyle={styles.dateStyle}
                                buttonTitleStyle={styles.buttonTitleStyle}
                                buttonStyle={styles.buttonStyle}
                                cancelStyle={styles.cancelStyle}
                                addOn={addOn}
																language={language}
																injectedContent={content}			
                            />
                            <Space height={24} />
                        </React.Fragment>
                    )}

                    {postpaidProvisionedOneOffAddOns && postpaidProvisionedOneOffAddOns.map((addOn, i) =>
                        <React.Fragment key={i}>
                            <ProductAddon
                                iconColor={colors.white}
                                style={styles.addonStyle}
                                placeholderStyle={styles.balanceItem}
                                disabled={true}
                                product={{
                                    ...addOn.product,
                                    expiration: addOn.expiryDateTime
                                }}
																language={language}
																injectedContent={content}
                            />
                            <Space height={24} />
                        </React.Fragment>
                    )}

                    {/* {(!provisionedRecurringAddOns || provisionedRecurringAddOns.length === 0) &&
                        <ProductNoPlan
                            onPress={() => void navigation.navigate('Plans')}
                            titleStyle={styles.bold}
                            style={styles.descriptionnNoPlanStyle}
                            buttonStyle={styles.buttonStyle}
                            containerStyle={styles.noPlanContainerStyle}
                            buttonTitle={content.dashboard.button}
                            description={content.dashboard.planDescription}
                        />
                    } */}
                </View>
            </Animated.ScrollView>
            {/* <Space height={8} /> */}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        // flex: 1,
        width: Dimensions.get('window').width,
        backgroundColor: backgroundColors.darkCharcoal,
    },
    headerContainer: {
        justifyContent: 'center',
        height: 250,
        backgroundColor: colors.darkCharcoal,
    },
    headerContent: {
        justifyContent: 'center',
        ...adaptive.wrapperPadding,
        backgroundColor: colors.darkCharcoal,
        borderColor: colors.darkCharcoal,
        flex: 1
    },
    branding: {
        // height: BRANDING_HEIGHT,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    dials: {
        // height: MAX_DIALS_HEIGHT,
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'flex-end',
        zIndex: -1,
    },
    expiration: {
        height: EXPIRATION_HEIGHT,
    },
    description: {
        textAlign: 'center'
    },
    line: {
        borderColor: colors.subtle,
        borderWidth: StyleSheet.hairlineWidth,
    },
    button: {
        backgroundColor: colors.brightOrange
    },
    content: {
        paddingTop: 12,
        ...adaptive.wrapperPadding,
        paddingBottom: 24,
        zIndex: 0
    },
    row: {
        flexDirection: 'row'
    },
    bold: {
        ...typography.bold
    },
    moondialStyle: {
        ...typography.lightbold,
    },
    buttonStyle: {
        backgroundColor: backgroundColors.brightOrange
    },
    titleStyle: {
        color: colors.white,
        ...typography.demi,
        fontSize: 16,
        lineHeight: 24
    },
    cashContainer: {
        padding: 24,
        backgroundColor: backgroundColors.darkCharcoal,
        borderRadius: radius.medium,
        elevation: 8,
    },
    priceStyle: {
        fontSize: 32,
        lineHeight: 48,
        fontWeight: '700',
        color: colors.white
    },
    descriptionStyle: {
        fontSize: 16,
        lineHeight: 24,
        color: colors.white,
        ...typography.demi,
    },
    descriptionnNoPlanStyle: {
        fontSize: 16,
        lineHeight: 24,
        color: colors.srTextInput,
        ...typography.demi,
    },
    descriptionContentStyle: {
        fontSize: 18,
        lineHeight: 24,
        color: colors.white,
        ...typography.demi
    },
    dateStyle: {
        fontSize: 18,
        lineHeight: 24,
        color: colors.white,
        ...typography.demi
    },
    buttonTitleStyle: {
        ...typography.lightbold,
        fontSize: 20
    },
    cancelStyle: {
        color: colors.white,
        textAlign: 'center'
    },
    noPlanContainerStyle: {
        backgroundColor: colors.white,
        ...adaptive.noPlanContainerHeight
    },
    addonStyle: {
        backgroundColor: colors.darkCharcoal
    },
    balanceItem: {
        backgroundColor: colors.srGreen
    },
    numberText: {
        textAlign: 'center',
        ...typography.bold,
        fontSize: 24,
        color: colors.white
    },
    headerMain: {
        flexDirection: 'row',
        justifyContent: 'space-evenly'
    },
    amountDue: {
    },
    amountDueTitle: {
        textAlign: 'right',
        ...typography.bold,
        fontSize: 18,
        color: 'transparent'
    },
    amountDueText: {
        textAlign: 'right',
        ...typography.bold,
        fontSize: 18,
        color: 'transparent'
    },
    rowContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%'
    },
    numberContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
    },
    planContainer: {
        padding: 24,
        backgroundColor: backgroundColors.darkCharcoal,
        borderRadius: radius.medium,
        elevation: 8,
        // ...shadows.shadowByElevation(8),
        borderColor: colors.white,
        borderWidth: 1
    },
})

